// 2024/common/Header.tsx
import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { StileGtagEventName } from '../../../../types/services';

import { Button } from '../Button';
import { Link } from '../Link';
import { Logo } from '../Logo';
import { TrialButton } from '../TrialButton/TrialButton';
import './Header.css';

// useLayoutEffect warns when run during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export type HeaderLinkSection = {
  title: string;
  links: { text: string; url: string }[];
};

type HeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  headerLinks: HeaderLinkSection[];
  enquireNowGtagEvent?: StileGtagEventName;
  /** Height of an element above the fixed header (e.g. BrandBar). The header
   *  will start at this offset and slide up as the user scrolls. */
  topOffset?: number;
};

export const Header = ({
  headerLinks,
  enquireNowGtagEvent = 'enquire_now_au',
  topOffset = 0,
  ...props
}: React.PropsWithChildren<HeaderProps>) => {
  const [mobileMenuShown, setMobileMenuShown] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const headerHeight = useRef(0);
  const [trialLabel, setTrialLabel] = React.useState('Connect with a Curriculum Specialist');

  // Function to calculate dropdown height with fallbacks
  const calculateDropdownHeight = (ul: HTMLElement): number => {
    // First try: get the computed height
    let height = ul.offsetHeight;

    // If height is 0 or very small, try alternative methods
    if (height <= 10) {
      // Method 1: Calculate based on children
      const children = ul.children;
      let calculatedHeight = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        calculatedHeight += child.offsetHeight;
      }

      // Add padding from CSS (16px top + 16px bottom = 32px)
      calculatedHeight += 32;

      if (calculatedHeight > 0) {
        height = calculatedHeight;
      }
    }

    // If still no height, use a reasonable fallback
    if (height <= 10) {
      // Estimate height based on number of links (assuming ~40px per link + padding)
      const linkCount = ul.children.length;
      height = Math.max(400, linkCount * 40 + 32);
    }

    return height;
  };

  // Function to set dropdown height with retry mechanism
  const setDropdownHeight = (ul: HTMLElement, retryCount = 0) => {
    if (!headerWrapperRef.current) return;

    const height = calculateDropdownHeight(ul);

    if (height > 10) {
      headerWrapperRef.current.style.setProperty('--dropdown-height', `${height}px`);
    } else if (retryCount < 3) {
      // Retry after a short delay if height calculation failed
      setTimeout(() => setDropdownHeight(ul, retryCount + 1), 50);
    } else {
      // Final fallback
      headerWrapperRef.current.style.setProperty('--dropdown-height', '400px');
    }
  };

  useEffect(() => {
    const checkWindowSize = () => {
      if (window.innerWidth <= 1220) {
        setTrialLabel('Enquire Now');
      } else {
        setTrialLabel('Connect with a Curriculum Specialist');
      }
    };
    checkWindowSize();
    window.addEventListener('resize', checkWindowSize);
    return () => window.removeEventListener('resize', checkWindowSize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth <= 768) {
        setHeaderHidden(false);
        return;
      }
      const currentScrollY = window.scrollY;
      if (headerHeight.current === 0 && headerWrapperRef.current) {
        headerHeight.current = headerWrapperRef.current.offsetHeight;
      }

      // Slide the fixed header up in sync with the brand-bar scrolling away
      if (topOffset > 0 && headerWrapperRef.current) {
        const dynamicTop = Math.max(0, topOffset - currentScrollY);
        headerWrapperRef.current.style.top = `${dynamicTop}px`;
      }

      if (currentScrollY > headerHeight.current) {
        if (currentScrollY > lastScrollY.current) {
          setHeaderHidden(true);
        } else {
          setHeaderHidden(false);
        }
      } else {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [topOffset]);

  // Layout effect (not a passive effect): the offset padding for the fixed
  // header must be applied — and the previous page's padding removed — before
  // Gatsby's ScrollHandler scrolls to the top on client-side navigation, so the
  // page never paints (or gets scrolled) with a stale offset.
  useIsomorphicLayoutEffect(() => {
    // Gatsby's router focuses #gatsby-focus-wrapper on every client-side
    // navigation (plain .focus(), no preventScroll), which scrolls the focused
    // element into view. The offset padding must therefore live ON that wrapper
    // — keeping its top edge at y=0 — rather than on <body>. With the padding
    // on <body> the wrapper starts at y=<header height>, so the focus call
    // scrolled every page to scrollY = header height instead of 0 right after
    // navigation. Fall back to <body> outside Gatsby (e.g. Storybook).
    const offsetTarget = document.getElementById('gatsby-focus-wrapper') ?? document.body;
    const updateBodyPadding = () => {
      if (window.innerWidth <= 768) {
        offsetTarget.style.paddingTop = '0';
        // Clear stale inline top from desktop scroll tracking so it
        // doesn't push the header down when position switches to relative
        if (headerWrapperRef.current) {
          headerWrapperRef.current.style.top = '';
        }
        return;
      }
      if (headerWrapperRef.current) {
        const height = headerWrapperRef.current.offsetHeight;
        offsetTarget.style.paddingTop = `${height + topOffset}px`;
        headerHeight.current = height;

        // Set initial top position for the fixed header
        if (topOffset > 0) {
          const dynamicTop = Math.max(0, topOffset - window.scrollY);
          headerWrapperRef.current.style.top = `${dynamicTop}px`;
        }
      }
    };
    updateBodyPadding();
    window.addEventListener('resize', updateBodyPadding);
    return () => {
      window.removeEventListener('resize', updateBodyPadding);
      offsetTarget.style.paddingTop = '0';
    };
  }, [topOffset]);

  // Close mobile menu when window expands beyond mobile width
  useEffect(() => {
    const handleMobileMenuResize = () => {
      if (window.innerWidth > 768 && mobileMenuShown) {
        setMobileMenuShown(false);
      }
    };

    window.addEventListener('resize', handleMobileMenuResize);
    return () => window.removeEventListener('resize', handleMobileMenuResize);
  }, [mobileMenuShown]);

  return (
    <div
      ref={headerWrapperRef}
      className={`header-wrapper ${headerHidden ? 'header--hidden' : ''}`}
      style={topOffset > 0 ? { top: `${topOffset}px` } : undefined}
    >
      <header {...props}>
        <div className="content">
          <Link url="/">
            <Logo />
          </Link>
          <nav className="desktopNav">
            {headerLinks.map((section) => (
              <div
                key={section.title}
                onMouseEnter={(e) => {
                  const ul = (e.currentTarget as HTMLElement).querySelector('ul');
                  if (ul) {
                    // Use requestAnimationFrame to ensure DOM is ready
                    requestAnimationFrame(() => {
                      setDropdownHeight(ul as HTMLElement);
                    });
                  }
                }}
                onMouseLeave={() => {
                  if (headerWrapperRef.current) {
                    headerWrapperRef.current.style.removeProperty('--dropdown-height');
                  }
                }}
              >
                <div>{section.title}</div>
                <ul>
                  {section.links.map((link) => (
                    <li key={link.url}>
                      <Link url={link.url}>{link.text}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <div className="grow"></div>
          <div className="buttons">
            <Button label="Teacher log in" style="secondary" href="https://stileapp.com/login" />
            <TrialButton gtagEvent={enquireNowGtagEvent} label={trialLabel} />
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuShown(!mobileMenuShown)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
              >
                <rect width="34" height="34" rx="6" fill="#00E65A" />
                <line
                  x1="10"
                  y1="22"
                  x2="24"
                  y2="22"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="10"
                  y1="16"
                  x2="24"
                  y2="16"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="10"
                  y1="10"
                  x2="24"
                  y2="10"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className={`content mobile-menu ${mobileMenuShown ? '--open' : ''}`}>
          <div className="buttons">
            <Button label="Teacher log in" style="secondary" href="https://stileapp.com/login" />
            <TrialButton gtagEvent={enquireNowGtagEvent} label={trialLabel} />
            <Button
              label={
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 7.27757L1.52852 11.7491C1.36122 11.9164 1.14829 12 0.889734 12C0.631179 12 0.418251 11.9164 0.250951 11.7491C0.0836502 11.5817 0 11.3688 0 11.1103C0 10.8517 0.0836502 10.6388 0.250951 10.4715L4.72243 6L0.250951 1.52852C0.0836502 1.36122 0 1.14829 0 0.889734C0 0.631179 0.0836502 0.418251 0.250951 0.250951C0.418251 0.0836502 0.631179 0 0.889734 0C1.14829 0 1.36122 0.0836502 1.52852 0.250951L6 4.72243L10.4715 0.250951C10.6388 0.0836502 10.8517 0 11.1103 0C11.3688 0 11.5817 0.0836502 11.7491 0.250951C11.9164 0.418251 12 0.631179 12 0.889734C12 1.14829 11.9164 1.36122 11.7491 1.52852L7.27757 6L11.7491 10.4715C11.9164 10.6388 12 10.8517 12 11.1103C12 11.3688 11.9164 11.5817 11.7491 11.7491C11.5817 11.9164 11.3688 12 11.1103 12C10.8517 12 10.6388 11.9164 10.4715 11.7491L6 7.27757Z"
                    fill="white"
                  />
                </svg>
              }
              style={'dark'}
              href="#"
              onClick={() => setMobileMenuShown(false)}
            />
          </div>
          <nav className="mobileNav">
            {headerLinks.map((section) => (
              <ul key={section.title}>
                <li>
                  <span>{section.title}</span>
                  <ul>
                    {section.links.map((link) => (
                      <li key={link.url}>
                        <Link url={link.url}>{link.text}</Link>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            ))}
          </nav>
        </div>
      </header>
    </div>
  );
};
